import { useIsMobile } from "@/hooks/useIsMobile";
import { useKeyPressed } from "@/hooks/useKeyPressed";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { useOnClickOutside } from "@/hooks/useOnClickOutside";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * FloatingDropdownSelect
 * Bir native <select> yerine custom dropdown kullanan, Select.js ile aynı
 * görünümde bir bileşen.
 */
const FloatingDropdownSelect = ({
  id,
  label,
  error,
  required = false,
  disabled = false,
  value,
  onChange,
  options = [],
  placeholder = "Seçiniz",
  className,
  dropdownClassName,
  itemClassName,
  renderOption,
  returnOption = false,
}) => {
  const { t } = useTranslationDetector();
  const isMobile = useIsMobile();
  const escapePressed = useKeyPressed("Escape");
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [shouldRenderPortal, setShouldRenderPortal] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRenderWebDropdown, setShouldRenderWebDropdown] = useState(false);
  const [isWebDropdownAnimating, setIsWebDropdownAnimating] = useState(false);

  const normalizedOptions = useMemo(() => {
    return (options || []).map((opt) =>
      typeof opt === "object"
        ? { value: opt.value, label: opt.label, ...opt }
        : { value: opt, label: String(opt) },
    );
  }, [options]);

  const selectedOption = useMemo(
    () => normalizedOptions.find((opt) => opt.value === value) || null,
    [normalizedOptions, value],
  );

  const displayLabel = selectedOption?.label || placeholder;
  const hasValue = Boolean(selectedOption);
  const shouldFloat = true;

  const handleClickOutside = useCallback(() => {
    if (!isMobile && isOpen) {
      setIsOpen(false);
    }
  }, [isMobile, isOpen]);

  useOnClickOutside(containerRef, handleClickOutside);
  useLockBodyScroll(shouldRenderPortal && isMobile);

  useEffect(() => {
    if (escapePressed && isOpen) {
      setIsOpen(false);
    }
  }, [escapePressed, isOpen]);

  useEffect(() => {
    if (isOpen && isMobile) {
      setShouldRenderPortal(true);
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else if (!isOpen && isMobile) {
      setIsAnimating(false);
      const timer = setTimeout(() => setShouldRenderPortal(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (isOpen && !isMobile) {
      setShouldRenderWebDropdown(true);
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsWebDropdownAnimating(true));
      });
      return () => cancelAnimationFrame(raf);
    } else if (!isOpen && !isMobile) {
      setIsWebDropdownAnimating(false);
      const timer = setTimeout(() => setShouldRenderWebDropdown(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isMobile]);

  const containerClasses = useMemo(
    () => clsx("flex flex-col gap-2", className),
    [className],
  );

  const controlClasses = useMemo(
    () => clsx("relative", disabled && "cursor-not-allowed opacity-75"),
    [disabled],
  );

  const buttonClasses = useMemo(
    () =>
      clsx(
        "w-full appearance-none rounded-md border bg-white px-4 py-[11px] text-left font-medium transition-all duration-200 focus:outline-none",
        error
          ? "border-danger focus:border-danger focus:ring-danger focus:ring-2"
          : "border-border focus:border-primary focus:ring-primary focus:ring-2",
        disabled && "bg-gray-100",
      ),
    [error, disabled, shouldFloat],
  );

  const labelClasses = useMemo(
    () =>
      clsx(
        "pointer-events-none absolute left-4 flex items-center gap-1 px-2 text-xs transition-all duration-200",
        disabled
          ? "text-gray-400"
          : error
            ? "text-danger"
            : "text-foreground-muted",
        shouldFloat ? "-top-2 bg-white" : "top-1/2 -translate-y-1/2 text-base",
      ),
    [shouldFloat, error, disabled],
  );

  const arrowClasses = useMemo(
    () =>
      clsx(
        "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 transition-colors",
      ),
    [],
  );

  const overlayClasses = useMemo(
    () =>
      clsx(
        "fixed inset-0 z-50 bg-black/50 transition-opacity duration-300",
        isAnimating ? "opacity-100" : "opacity-0",
      ),
    [isAnimating],
  );

  const portalContainerClasses = useMemo(
    () =>
      clsx(
        "bg-background-global fixed inset-x-0 bottom-0 z-50 flex max-h-[70vh] flex-col rounded-t-2xl",
        "transform transition-transform duration-300",
        isAnimating ? "translate-y-0" : "translate-y-full",
      ),
    [isAnimating],
  );

  const webDropdownClasses = useMemo(() => {
    return clsx(
      "absolute top-full left-1/2 z-50 mt-2 w-full -translate-x-1/2 rounded-2xl bg-white shadow-[0_16px_32px_rgba(15,23,42,0.12)]",
      "origin-top transition-all duration-200 ease-out",
      isWebDropdownAnimating
        ? "translate-y-0 scale-100 opacity-100"
        : "-translate-y-1 scale-95 opacity-0",
      dropdownClassName,
    );
  }, [isWebDropdownAnimating, dropdownClassName]);

  const renderOptionItem = useCallback(
    (option, isSelected) => {
      if (typeof renderOption === "function") {
        return renderOption(option, isSelected);
      }
      return option.label;
    },
    [renderOption],
  );

  const handleSelect = useCallback(
    (option) => {
      setIsOpen(false);
      if (disabled) return;

      if (returnOption) {
        onChange?.(option);
      } else {
        const syntheticEvent = {
          target: { value: option?.value ?? "" },
          value: option?.value ?? "",
          option,
        };
        onChange?.(syntheticEvent);
      }
    },
    [disabled, onChange, returnOption],
  );

  const renderOptions = useCallback(
    () => (
      <div
        className={clsx(
          "relative flex md:max-h-72 max-h-[500px] pb-4 flex-col overflow-y-auto rounded-2xl",
          isMobile && "gap-3 px-4",
        )}
        role="listbox"
      >
        {normalizedOptions.length === 0 && (
          <div className="text-foreground-muted px-4 py-3 text-sm">
            {placeholder}
          </div>
        )}
        {normalizedOptions.map((option) => {
          const isSelected = option.value === selectedOption?.value;
          return (
            <button
              key={option.value}
              type="button"
              className={clsx(
                "flex w-full items-center text-left text-base transition-colors bg-white",
                isMobile ? [
                  // Mobile styles
                  "rounded-lg h-[50px] px-3 justify-between",
                  "hover:bg-primary-weak/40 focus:bg-primary-weak/40",
                  isSelected ? "border-primary border text-primary" : "text-foreground",
                ] : [
                  // Desktop styles
                  "md:border-primary-weak md:border-b md:last:border-b-0",
                  "md:h-auto md:py-[17px] md:px-5",
                  "md:hover:bg-primary-weak/40 md:focus:bg-primary-weak/40",
                  isSelected ? "md:text-primary" : "md:text-foreground",
                ],
                itemClassName,
              )}
              onClick={() => handleSelect(option)}
            >
              <span className={clsx(
                "flex w-full items-center",
                isMobile ? "gap-[12px] h-[50px]" : "md:h-[21px]"
              )}>
                {isMobile && (
                  <span
                    className={clsx(
                      "flex h-5 w-5 items-center justify-center rounded-full border transition-colors",
                      isSelected
                        ? "border-primary bg-primary"
                        : "border-border bg-white",
                    )}
                  >
                    {isSelected && (
                      <span className="inline-block h-2 w-2 rounded-full bg-white" />
                    )}
                  </span>
                )}
                <span className="block flex-1 truncate pr-2">
                  {renderOptionItem(option, isSelected)}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    ),
    [
      normalizedOptions,
      placeholder,
      selectedOption,
      handleSelect,
      renderOptionItem,
      itemClassName,
    ],
  );

  const toggleDropdown = useCallback(() => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  }, [disabled]);

  return (
    <div className={containerClasses} ref={containerRef}>
      <div className={controlClasses}>
        <button
          id={id}
          type="button"
          className={buttonClasses}
          onClick={toggleDropdown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          {label && (
            <span className={labelClasses}>
              <span>{label}</span>
            </span>
          )}
          <span className="block max-w-[calc(100%-20px)] truncate">
            {displayLabel || placeholder}
          </span>
          <span className={arrowClasses}>
            <FontAwesomeIcon
              icon={byPrefixAndName.fas["chevron-down"]}
              className={clsx(
                "h-4 w-4 transition-colors",
                error
                  ? "text-danger"
                  : isOpen
                    ? "text-primary"
                    : "text-foreground-muted",
                disabled && "text-gray-400",
              )}
            />
          </span>
        </button>

        {/* Web dropdown */}
        {!isMobile && shouldRenderWebDropdown && (
          <div className={webDropdownClasses}>{renderOptions()}</div>
        )}

        {/* Mobile portal */}
        {shouldRenderPortal &&
          isMobile &&
          createPortal(
            <>
              <div
                className={overlayClasses}
                onClick={() => setIsOpen(false)}
              />
              <div className={portalContainerClasses}>
                <div className="flex items-center justify-between px-4 py-5">
                  <span className="w-full text-center text-lg font-semibold">
                    {label || placeholder}
                  </span>
                  <button onClick={() => setIsOpen(false)} className="self-end">
                    <FontAwesomeIcon
                      icon={byPrefixAndName.far["xmark"]}
                      className="text-2xl"
                    />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto">{renderOptions()}</div>
              </div>
            </>,
            document.body,
          )}
      </div>

      {error && (
        <p className="text-danger flex items-center gap-1 pl-1 text-xs">
          <FontAwesomeIcon icon={byPrefixAndName.far["circle-exclamation"]} />
          <span>
            {typeof error === "string" ? error : t("common.required")}
          </span>
        </p>
      )}
    </div>
  );
};

export default FloatingDropdownSelect;
